import { coverageSpansDomain } from './interval-coverage.js';
import { isNoSensorData, NO_SENSOR_DATA_TRIGGER } from './sensor-health.js';
import { parseValueTrigger, triggerToBoundedInterval as triggerToBoundedIntervalShared } from './value-trigger.js';
import { optionalOutcome, SET_ANSWER_TRIGGER, participationOutcome } from './grading-mode.js';
import { activeFeedbackForBranch, resolveMatchedBranch } from './conditional-branch.js';
import { addVersionWith, applyVersionHead, pickVersion, removeVersionWith, writeVersionWith } from './versions.js';
import { pickScenarioBranch } from './scenario.js';
/**
 * GPS measures a DISTANCE from a target, so its intervals are radii: `lt:50` means "within 50
 * metres", which starts at 0 and not at -Infinity. There is no ceiling, because a student can be
 * arbitrarily far away.
 */
function triggerToBoundedInterval(trigger) {
    return triggerToBoundedIntervalShared(trigger, 0, Infinity);
}
const EARTH_RADIUS_METERS = 6371000; // mean radius; adequate for tolerance-radius-scale distances
/**
 * Great-circle distance between two lat/lng points, in meters. Hand-rolled — no geo
 * dependency exists anywhere in this repo, and packages/blocks' only dependency, mathjs,
 * has no geo capability — following this file's own established convention of self-contained
 * math rather than adding a new package. Exported so the editor can reuse it for the
 * teacher-authoring cross-block sanity check (see gps-block.tsx).
 */
export function haversineDistanceMeters(a, b) {
    const toRad = (deg) => (deg * Math.PI) / 180;
    const dLat = toRad(b.lat - a.lat);
    const dLng = toRad(b.lng - a.lng);
    const lat1 = toRad(a.lat);
    const lat2 = toRad(b.lat);
    const sinDLat = Math.sin(dLat / 2);
    const sinDLng = Math.sin(dLng / 2);
    const h = sinDLat * sinDLat + Math.cos(lat1) * Math.cos(lat2) * sinDLng * sinDLng;
    const c = 2 * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
    return EARTH_RADIUS_METERS * c;
}
const EPS = (n) => 1e-9 * Math.max(1, Math.abs(n));
/** GPS measures a DISTANCE from a target, so its domain starts at zero: there is no negative
 * radius. No ceiling - a student can be arbitrarily far away. */
const GPS_DOMAIN_LO = 0;
function matchesValueTrigger(trigger, distance) {
    const parsed = parseValueTrigger(trigger);
    if (!parsed)
        return false;
    const { type, n1, n2 } = parsed;
    switch (type) {
        case 'exact': return Math.abs(distance - n1) < EPS(n1);
        case 'lt': return distance < n1;
        case 'lte': return distance <= n1;
        case 'gt': return distance > n1;
        case 'gte': return distance >= n1;
        case 'range': return distance >= Math.min(n1, n2) && distance <= Math.max(n1, n2);
    }
}
/**
 * Returns the feedback states that match a student's GPS capture.
 *
 * Evaluation sequence (same structural-gate-before-survey shape as evaluateNumerical's
 * incomplete_answer handling — "can this reading be scored at all" is orthogonal to grading
 * mode, so it's checked in the same tier, before survey mode gets consulted):
 * 1. optional mode → any_response, touched or not, accurate or not — no gate or correctness
 *    check at all for this mode (see positiveTriggerForMode in use-input-block-feedback.ts).
 * 2. Untouched (graded/survey only) → unanswered.
 * 3. Accuracy gate — reading's own accuracy is worse (numerically larger) than
 *    accuracyCutoff → terminal `poor_accuracy` state, never reaches the tolerance-radius
 *    check at all. Fires even in survey mode (a garbage reading isn't real participation).
 * 4. survey mode → any_answer (any accurate-enough capture counts as participation).
 * 5. graded mode: haversine distance to target <= toleranceRadius → `correct`; else all
 *    matching targeted distance-feedback triggers fire in parallel (same lt/lte/gt/gte/
 *    range/exact vocabulary numerical/slider use, applied to distance instead of a signed
 *    value), else `incorrect`.
 */
/**
 * The targeted states a DISTANCE fires outside graded: the value triggers it matches, plus Set
 * Answer when it is inside the accepted radius.
 *
 * Shared by survey and optional so the two cannot drift. GPS's triggers are distance FROM THE
 * TARGET, which is why its Set Answer means "within the tolerance radius" — a band at distance
 * zero — rather than a value band like the other sensors'.
 */
function participationMatches(distance, states, toleranceRadius) {
    return states.filter(st => st.type !== 'targeted' ? false
        : st.trigger === SET_ANSWER_TRIGGER ? distance <= toleranceRadius
            : matchesValueTrigger(st.trigger, distance));
}
export function evaluateGPS(answer, 
/** What the receiver managed. `accuracy` used to sit on the answer; `compass.ts`'s own doc named
 *  that nesting a mistake, so it moved out here with the rest. */
health, blockData, resolvedVariables = {}, 
/** The leader's variant in force — see `evaluateDropdown`'s own note. */
scenarioId) {
    const { touched, lat, lng } = answer;
    const accuracy = health.positionAccuracy ?? 0;
    const { toleranceRadius, accuracyCutoff, gradingMode, feedback, conditionalEquation, conditionalBranches, scenarioBranches } = blockData;
    // Branches are resolved ONLY in graded mode, matching every other branching block: in
    // Participation or Optional there is no correct answer for a branch to select.
    /* A FOLLOWER'S SCENARIO ROW REPLACES the BLOCK's numeric branch, never joins it. The row's
       OWN nested branching is a different thing — see `rolesForScenarioRow`. */
    const followed = gradingMode === 'graded' && !!scenarioBranches && scenarioBranches.length > 0;
    const scenarioRow = followed ? pickScenarioBranch(scenarioBranches, scenarioId) : null;
    const branchesConfigured = !followed && gradingMode === 'graded' && !!conditionalBranches && conditionalBranches.length > 0;
    const matchedBranch = branchesConfigured
        ? resolveMatchedBranch(conditionalEquation ?? '', conditionalBranches, resolvedVariables, 'degrees')
        : null;
    /** Either selector, one answer: the row's target when followed, the branch's when branched. */
    const selected = scenarioRow ?? matchedBranch;
    /** Is SOMETHING meant to choose the target? Both selectors fail closed the same way. */
    const targeted = followed || branchesConfigured;
    // Incomplete coverage resolves to NULL ISLAND. The target has to be *something* for the distance
    // maths to run, and 0,0 is the one coordinate no field site is at — so every reading grades
    // incorrect, which is the agreed behaviour for a gap that reaches a student: never silence.
    // `activeFeedbackForBranch` independently strips targeted states in this same case, so the
    // fallback is incorrect by two mechanisms rather than relying on either alone.
    const targetLat = selected ? selected.targetLat : targeted ? 0 : blockData.targetLat;
    const targetLng = selected ? selected.targetLng : targeted ? 0 : blockData.targetLng;
    // THE BRANCH'S OWN feedback, like every other branching block. `matchedBranch?.feedback` is
    // optional-chained for branches saved before the field existed: those fall back to the block's,
    // which is what they have always used.
    //
    // The unresolved fallback still strips every TARGETED state, so a leftover distance trigger
    // cannot fire against Null Island and pre-empt Incorrect.
    const activeFeedback = activeFeedbackForBranch(selected?.feedback, feedback, targeted);
    /**
     * OPTIONAL fires targeted states too, which it did not.
     *
     * This returned `any_response` before the reading was even looked at, so a distance trigger
     * authored in optional could never fire and the editor listed chips that were dead. Survey has
     * always reached the matcher (see its own branch below); optional was the odd one out.
     *
     * The GATES stay above it, unchanged: a blank or a dead sensor has no reading to measure, so
     * there is no distance to match and the submission is a blank — which `blankTargeted` is for.
     */
    if (gradingMode === 'optional') {
        // OPTIONAL is INERT: the catch-all, whatever the student did. See `targetedFiresIn`.
        return optionalOutcome(activeFeedback.states);
    }
    // The device failed with no way round it. ABOVE the blank check, because `touched` is false in
    // both cases and this is the more specific one: a student who could not measure is not a student
    // who did not try. Below `optional`, which short-circuits everything by design — a block nobody
    // is grading has no problem to route. GPS reaches this branch the most: it is the one sensor with
    // no manual bypass at all, so a denied permission has nowhere else to go.
    if (isNoSensorData(health)) {
        const noData = activeFeedback.states.find(s => s.type === 'default' && s.trigger === NO_SENSOR_DATA_TRIGGER);
        return noData ? [noData] : [];
    }
    if (!touched) {
        const unanswered = activeFeedback.states.find(s => s.type === 'default' && s.trigger === 'unanswered');
        return unanswered ? [unanswered] : [];
    }
    if (accuracy > accuracyCutoff) {
        const poorAccuracy = activeFeedback.states.find(s => s.type === 'default' && s.trigger === 'poor_accuracy');
        return poorAccuracy ? [poorAccuracy] : [];
    }
    const distance = haversineDistanceMeters({ lat: targetLat, lng: targetLng }, { lat, lng });
    /** The targeted states this reading fires. ONE matcher, used by every grading mode — see
     *  `participationOutcome`. Distance from the target is measurable whether or not any distance
     *  is the RIGHT one, so it means something in a survey. */
    const targetedForDistance = () => participationMatches(distance, activeFeedback.states, toleranceRadius);
    // Targeted value states fire here too. Whether one CAN fire is analysed against the accepted
    // band, and outside graded there is no band in force — correct is never checked, so nothing is
    // subsumed by it. The editor's own analysis is mode-aware to match.
    if (gradingMode === 'survey') {
        return participationOutcome(activeFeedback.states, 'survey', targetedForDistance());
    }
    if (distance <= toleranceRadius) {
        const correct = activeFeedback.states.find(s => s.type === 'default' && s.trigger === 'correct');
        return correct ? [correct] : [];
    }
    const matched = targetedForDistance();
    if (matched.length > 0)
        return matched;
    const incorrect = activeFeedback.states.find(s => s.type === 'default' && s.trigger === 'incorrect');
    return incorrect ? [incorrect] : [];
}
/**
 * Coverage as BOUNDED intervals, so a gap can say which end it owns.
 *
 * It used to drop the inclusivity, and that is exactly the ambiguity it produced: a target at
 * `range:40:60` is closed at both ends, so the leftover below it stops just short of 40 — but the
 * chip printed "Between 0 and 40", which reads as owning 40 too. Two cards claiming one distance.
 *
 * `triggerToBoundedInterval` already knows each trigger's rules (`lt` open at the top, `gte`
 * closed at the bottom, a range closed at both), so carrying them is a matter of not discarding
 * them. Mirrors `computeDefaultIncorrectGaps` in numerical.ts, which has modelled it this way
 * since the predicate census.
 *
 * null accepted range means tolerance is not a usable number yet — nothing is "Correct".
 */
function mergeGpsCoverageIntervals(acceptedRange, feedbackTriggers) {
    // The accepted band is a radius: closed at both ends, like the `within` it comes from.
    const intervals = acceptedRange
        ? [{ ...acceptedRange, loInclusive: true, hiInclusive: true }] : [];
    for (const trigger of feedbackTriggers) {
        const iv = triggerToBoundedInterval(trigger);
        // 'exact' contributes no real interval coverage — a single point cannot close a real gap, the
        // same exclusion numerical's own merge makes.
        if (!iv || iv.lo === iv.hi)
            continue;
        intervals.push(iv);
    }
    const sorted = [...intervals].sort((a, b) => a.lo - b.lo);
    const merged = [];
    for (const iv of sorted) {
        const last = merged[merged.length - 1];
        // Touching counts as overlapping only when at least one side is closed there: `lt:40` and
        // `gt:40` meet at 40 and still leave it uncovered, which is what keeps a single-point gap real.
        const touches = last && (iv.lo < last.hi + EPS(last.hi)
            && (iv.lo < last.hi - EPS(last.hi) || iv.loInclusive || last.hiInclusive));
        if (last && touches) {
            if (iv.hi > last.hi) {
                last.hi = iv.hi;
                last.hiInclusive = iv.hiInclusive;
            }
            else if (iv.hi === last.hi)
                last.hiInclusive = last.hiInclusive || iv.hiInclusive;
        }
        else
            merged.push({ ...iv });
    }
    return merged;
}
/** Whether Default Incorrect is genuinely unreachable — every distance from 0 to infinity is
 * covered by either Correct or some targeted distance-feedback trigger. */
export function isGpsDefaultIncorrectUnreachable(acceptedRange, feedbackTriggers) {
    const merged = mergeGpsCoverageIntervals(acceptedRange, feedbackTriggers);
    return coverageSpansDomain(merged, GPS_DOMAIN_LO, Infinity);
}
/**
 * The distance gaps that actually reach Default Incorrect. Empty means unreachable (this is the
 * inverse of `isGpsDefaultIncorrectUnreachable`).
 *
 * A gap's boundary is the MIRROR of the span beside it: coverage closed at 40 leaves a gap open at
 * 40, because 40 is taken. That is the whole point of carrying the flags.
 */
export function computeGpsDefaultIncorrectGaps(acceptedRange, feedbackTriggers) {
    const merged = mergeGpsCoverageIntervals(acceptedRange, feedbackTriggers);
    if (merged.length === 0)
        return [{ lo: 0, hi: Infinity, loInclusive: true, hiInclusive: true }];
    const gaps = [];
    const first = merged[0];
    if (first.lo > GPS_DOMAIN_LO) {
        gaps.push({ lo: GPS_DOMAIN_LO, hi: first.lo, loInclusive: true, hiInclusive: !first.loInclusive });
    }
    for (let i = 1; i < merged.length; i++) {
        const prev = merged[i - 1], cur = merged[i];
        if (cur.lo > prev.hi || (cur.lo === prev.hi && !prev.hiInclusive && !cur.loInclusive)) {
            gaps.push({ lo: prev.hi, hi: cur.lo, loInclusive: !prev.hiInclusive, hiInclusive: !cur.loInclusive });
        }
    }
    const last = merged[merged.length - 1];
    if (last.hi !== Infinity) {
        gaps.push({ lo: last.hi, hi: Infinity, loInclusive: !last.hiInclusive, hiInclusive: true });
    }
    return gaps;
}
function triggerToInterval(trigger) {
    const parsed = parseValueTrigger(trigger);
    if (!parsed)
        return null;
    const { type, n1, n2 } = parsed;
    if (type === 'exact')
        return { lo: n1, hi: n1 };
    if (type === 'lt' || type === 'lte')
        return { lo: 0, hi: n1 };
    if (type === 'gt' || type === 'gte')
        return { lo: n1, hi: Infinity };
    return { lo: Math.min(n1, n2), hi: Math.max(n1, n2) };
}
/** Whether a targeted feedback trigger's entire domain already falls inside Correct's
 * accepted range — i.e. it can never actually fire, since Correct is checked first and is
 * terminal (see evaluateGPS's sequence). Deliberately ignores lt/lte-vs-gt/gte strictness —
 * matching numerical.ts's own isValueFeedbackFullySubsumed precedent — since treating every
 * bound as closed here is a safe *over*-approximation for containment: if the closed version
 * fits fully inside acceptedRange, the real (possibly open) one necessarily does too. */
export function isGpsFeedbackFullySubsumed(trigger, acceptedRange) {
    if (!acceptedRange)
        return false;
    const target = triggerToInterval(trigger);
    if (!target)
        return false;
    return target.lo >= acceptedRange.lo - EPS(acceptedRange.lo) && target.hi <= acceptedRange.hi + EPS(acceptedRange.hi);
}
/** Whether two bounded intervals share at least one real point, respecting each side's own
 * open/closed boundary at a touching point (see the boundary-strictness note above). */
function gpsIntervalsOverlap(a, b) {
    if (a.hi < b.lo || b.hi < a.lo)
        return false;
    if (a.hi === b.lo)
        return a.hiInclusive && b.loInclusive;
    if (b.hi === a.lo)
        return b.hiInclusive && a.loInclusive;
    return true;
}
/** Whether a targeted feedback trigger's domain intersects Correct's accepted range at
 * all (partial overlap) — a weaker condition than isGpsFeedbackFullySubsumed. Correct's own
 * range is always closed at both ends (distance 0 and toleranceRadius are both "within"). */
export function doesGpsFeedbackOverlapAccepted(trigger, acceptedRange) {
    if (!acceptedRange)
        return false;
    const target = triggerToBoundedInterval(trigger);
    if (!target)
        return false;
    const accepted = { ...acceptedRange, loInclusive: true, hiInclusive: true };
    return gpsIntervalsOverlap(target, accepted);
}
/** Pairs of currently-authored targeted feedback triggers whose ranges overlap each
 * other — likely unintentional (a teacher probably meant non-overlapping bands like
 * 10-20m, 20-30m, but wrote two open-ended "farther than" conditions that both cover
 * everything past the smaller threshold instead). 'exact' triggers are excluded, mirroring
 * numerical.ts's findOverlappingFeedbackPairs — a specific callout value nested inside a
 * broader band is a common, deliberate authoring pattern, not a mistake. */
export function findGpsOverlappingFeedbackPairs(feedbackTriggers) {
    const parsed = feedbackTriggers
        .map(trigger => {
        const parsedTrigger = parseValueTrigger(trigger);
        if (!parsedTrigger || parsedTrigger.type === 'exact')
            return null;
        const interval = triggerToBoundedInterval(trigger);
        return interval ? { trigger, ...interval } : null;
    })
        .filter((x) => x !== null);
    const pairs = [];
    for (let i = 0; i < parsed.length; i++) {
        for (let j = i + 1; j < parsed.length; j++) {
            const a = parsed[i], b = parsed[j];
            if (gpsIntervalsOverlap(a, b))
                pairs.push([a.trigger, b.trigger]);
        }
    }
    return pairs;
}
/** A GPS block resolved to one of its versions — the target moves, the way it is read does not. */
export function applyGPSVersion(data, versionId) {
    const version = pickVersion(data.versions, versionId);
    if (!version)
        return data;
    // The cast carries the representation across: a block and its own versions always agree about
    // it, and `GPSKeyFields` deliberately does not pin which one — see its note.
    return {
        ...applyVersionHead(data, version),
        targetLat: version.targetLat,
        targetLng: version.targetLng,
        toleranceRadius: version.toleranceRadius,
    };
}
/** The target this block is currently holding — the key a version stores. */
function gpsKey(data) {
    return { targetLat: data.targetLat, targetLng: data.targetLng, toleranceRadius: data.toleranceRadius };
}
export function writeGPSVersion(current, versionId, next) {
    return writeVersionWith(current, versionId, next, gpsKey(next));
}
export function addGPSVersion(data, fromVersionId, newId) {
    return addVersionWith(data, fromVersionId, newId, gpsKey(data));
}
export function removeGPSVersion(data, versionId) {
    return removeVersionWith(data, versionId, applyGPSVersion);
}
//# sourceMappingURL=gps.js.map